/* eslint-disable complexity */
/**
 * React Hook Form validation hooks for RMC stepper
 * Provides clean, reusable validation logic following React best practices
 */

import {useLoadSpacingScenario} from '@hconnect/tuc-processes/fields/loadSpacing'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {OrderIntakeStep} from '@hconnect/tuc-processes/orderIntake/wizard/types'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useWatch} from 'react-hook-form'

import {useRmcCountryFeatures} from '../../shared/order-editor/hooks/useRmcCountryFeatures'
import {RmcOrderIntakeStepType} from '../config/stepTypes'

import {getStepValidationFields} from './stepValidationConfig'

/**
 * Hook for handling step navigation with validation
 */
export const useStepNavigation = (
  currentStep: OrderIntakeStep<RmcOrderIntakeStepType>,
  goToNextStep: () => void,
  goToPreviousStep: () => void
) => {
  const {trigger, control} = useOrderProcessFormContext()
  const [isCurrentStepValid, setIsCurrentStepValid] = useState(false) // Start pessimistic - require validation
  const regionFeatures = useRmcCountryFeatures()
  const loadSpacingScenario = useLoadSpacingScenario()

  // Get the step type from the current step
  const currentStepType = currentStep?.key as RmcOrderIntakeStepType

  // Get the fields to validate for the current step
  // Using useMemo to ensure fields update when step changes
  const fieldsToWatch = useMemo(() => getStepValidationFields(currentStepType), [currentStepType])

  // Instead of watching all fields at once (which doesn't work well with dynamic fields),
  // we'll watch specific known fields and filter based on current step
  // Use type assertion to handle dynamic field names
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const material = useWatch({control, name: 'material' as any})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contract = useWatch({control, name: 'contract' as any})
  const quantityValue = useWatch({control, name: 'quantity'})
  const plusLoadExpected = useWatch({control, name: 'plusLoadExpected'})
  const truckType = useWatch({control, name: 'truckType'})
  const concreteUsage = useWatch({control, name: 'concreteUsage'})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const unloadingMethod = useWatch({control, name: 'unloadingMethod' as any})
  const unloadingMethodCode = useWatch({control, name: 'unloadingMethod.unloadingMethodCode'})
  const pump = useWatch({control, name: 'unloadingMethod.pump'})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const spacingOrFlowRate = useWatch({control, name: 'loadSpacing' as any})
  const timeOnJobSite = useWatch({control, name: 'timeOnJobSite'})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dateTime = useWatch({control, name: 'dateTime' as any})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timeSlot = useWatch({control, name: 'timeSlot' as any})
  const additionalNotes = useWatch({control, name: 'additionalNotes'})
  const poNumber = useWatch({control, name: 'poNumber'})
  const driverInstructions = useWatch({control, name: 'driverInstructions'})

  // Contact fields for details step
  const orderPlacerName = useWatch({control, name: 'orderPlacer.name'})
  const orderPlacerPhoneNumber = useWatch({control, name: 'orderPlacer.phoneNumber'})
  const siteContactName = useWatch({control, name: 'siteContact.name'})
  const siteContactPhoneNumber = useWatch({control, name: 'siteContact.phoneNumber'})

  // Create a dependency array based on the current step's fields
  const watchedFieldValues = useMemo(() => {
    const values: Record<string, unknown> = {}
    fieldsToWatch.forEach((field) => {
      switch (field) {
        case 'material':
          values.material = material
          break
        case 'contract':
          values.contract = contract
          break
        case 'quantity':
          values['quantity'] = quantityValue
          break
        case 'plusLoadExpected':
          values['plusLoadExpected'] = plusLoadExpected
          break
        case 'truckType':
          values.truckType = truckType
          break
        case 'concreteUsage':
          values.concreteUsage = concreteUsage
          break
        case 'unloadingMethod':
          values.unloadingMethod = unloadingMethod
          break
        case 'unloadingMethod.unloadingMethodCode':
          values['unloadingMethod.unloadingMethodCode'] = unloadingMethodCode
          break
        case 'unloadingMethod.pump':
          values['unloadingMethod.pump'] = pump
          break
        case 'loadSpacing':
          values.loadSpacing = spacingOrFlowRate
          break
        case 'timeOnJobSite':
          values.timeOnJobSite = timeOnJobSite
          break
        case 'dateTime':
          values.dateTime = dateTime
          break
        case 'timeSlot':
          values.timeSlot = timeSlot
          break
        case 'additionalNotes':
          values.additionalNotes = additionalNotes
          break
        case 'poNumber':
          values.poNumber = poNumber
          break
        case 'driverInstructions':
          values.driverInstructions = driverInstructions
          break
        case 'orderPlacer.name':
          values['orderPlacer.name'] = orderPlacerName
          break
        case 'orderPlacer.phoneNumber':
          values['orderPlacer.phoneNumber'] = orderPlacerPhoneNumber
          break
        case 'siteContact.name':
          values['siteContact.name'] = siteContactName
          break
        case 'siteContact.phoneNumber':
          values['siteContact.phoneNumber'] = siteContactPhoneNumber
          break
      }
    })
    return values
  }, [
    fieldsToWatch,
    material,
    contract,
    quantityValue,
    plusLoadExpected,
    truckType,
    concreteUsage,
    unloadingMethod,
    unloadingMethodCode,
    pump,
    spacingOrFlowRate,
    timeOnJobSite,
    dateTime,
    timeSlot,
    additionalNotes,
    poNumber,
    driverInstructions,
    orderPlacerName,
    orderPlacerPhoneNumber,
    siteContactName,
    siteContactPhoneNumber
  ])

  /**
   * Validate current step fields
   */
  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    try {
      const fields = getStepValidationFields(currentStepType)
      if (fields.length === 0) {
        setIsCurrentStepValid(true)
        return true
      }

      // Optional fields that don't need empty check (validated by schema only)
      const optionalFields = ['additionalNotes', 'driverInstructions']

      // Disabled fields based on country configuration (should be skipped in validation)
      const disabledFields: string[] = []
      if (!regionFeatures.hasDriverInstructions) disabledFields.push('driverInstructions')
      if (!regionFeatures.hasNotes) disabledFields.push('additionalNotes')
      if (!regionFeatures.hasConcreteUsage) disabledFields.push('concreteUsage')
      if (!regionFeatures.hasTruckTypeSelector) disabledFields.push('truckType')
      if (!regionFeatures.hasUnloadingMethod) {
        disabledFields.push('unloadingMethod', 'unloadingMethod.unloadingMethodCode')
      }
      if (!regionFeatures.hasPumpSelector) {
        disabledFields.push('unloadingMethod.pump')
      }

      // Disabled fields based on load spacing scenario (intervals step)
      if (!loadSpacingScenario.loadSpace) {
        disabledFields.push('loadSpacing')
      }
      // If timeOnJobSite is false in the scenario, the timeOnJobSite field is disabled
      if (!loadSpacingScenario.timeOnJobSite) {
        disabledFields.push('timeOnJobSite')
      }

      // Check if any REQUIRED field is empty (excluding optional and disabled fields)
      const hasEmptyRequiredFields = fields.every((field) => {
        // Skip optional fields - they're validated by the schema if they have values
        if (optionalFields.includes(field)) {
          return false
        }

        // Skip disabled fields - they're not relevant for this country
        if (disabledFields.includes(field)) {
          return false
        }

        const fieldValue = watchedFieldValues[field]

        // Check for null, undefined, empty string, or empty array
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
          return true
        }
        if (Array.isArray(fieldValue) && fieldValue.length === 0) {
          return true
        }
        // For objects, check if they have meaningful values
        if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
          return Object.keys(fieldValue).length === 0
        }
        return false
      })

      // If there are empty required fields, the step is not valid
      if (hasEmptyRequiredFields) {
        setIsCurrentStepValid(false)
        return false
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
      const isValid = await trigger(fields.length === 1 ? fields[0] : (fields as any))

      setIsCurrentStepValid(isValid)
      return isValid
    } catch (error) {
      console.error('Step validation error:', error)
      setIsCurrentStepValid(false)
      return false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepType, trigger, watchedFieldValues])

  // Validate on initial load and when step changes
  useEffect(() => {
    void validateCurrentStep()
  }, [currentStepType, validateCurrentStep])

  // Re-validate when form fields change for the current step
  useEffect(() => {
    void validateCurrentStep()
  }, [watchedFieldValues, currentStepType, validateCurrentStep])

  /**
   * Handle next step navigation with validation
   */
  const handleNextStep = useCallback(async () => {
    const isValid = await validateCurrentStep()

    if (isValid) {
      goToNextStep()
    }
  }, [validateCurrentStep, goToNextStep])

  /**
   * Handle previous step navigation (no validation required)
   */
  const handlePreviousStep = useCallback(() => {
    goToPreviousStep()
  }, [goToPreviousStep])

  return {
    handleNextStep,
    handlePreviousStep,
    isCurrentStepValid,
    canProceedToNext: isCurrentStepValid
  }
}

/**
 * Hook for step-specific validation triggers
 * Useful for components that need to trigger validation on specific actions
 */
export const useStepValidationTrigger = (stepType: RmcOrderIntakeStepType) => {
  const {trigger} = useOrderProcessFormContext()

  /**
   * Trigger validation for current step
   */
  const triggerStepValidation = useCallback(async (): Promise<boolean> => {
    const fields = getStepValidationFields(stepType)
    if (fields.length === 0) return true

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
    return await trigger(fields.length === 1 ? fields[0] : (fields as any))
  }, [stepType, trigger])

  return {
    triggerStepValidation
  }
}
